import { useState } from 'react';

/** Stable id for the vendor description element. Imported by AppShell so the
 *  `aria-describedby` reference and the element's id can't silently diverge. */
export const VENDOR_SNAPSHOT_BLURB_ID = 'vendor-snapshot-blurb';
import type { Vendor, Category } from '../../../types';
import { CategoryGlyph } from '../../../components/primitives/CategoryGlyph';
import { XIcon } from '../../../components/primitives/Icon';
import { legibleOn } from '../../../utils/legibleOn';
import {
  isAmenityType,
  amenityFill,
  isStorefrontCategoryId,
  storefrontFill,
} from '../../../utils/amenities';
import { DEFAULT_CATEGORY_COLOR } from '../../../utils/categoryColors';
import { resolveGlyphKey } from '../../../utils/glyphKey';
import { LocationTag } from './parts/LocationTag';
import styles from './VendorSnapshot.module.css';

interface Props {
  vendor: Vendor;
  category?: Category | undefined;
  locationName?: string | undefined;
  onClose?: (() => void) | undefined;
  onViewDetails?: ((vendor: Vendor) => void) | undefined;
}

export function VendorSnapshot({
  vendor,
  category,
  locationName,
  onClose,
  onViewDetails,
}: Props) {
  const [prevSrc, setPrevSrc] = useState(vendor.image);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  if (vendor.image !== prevSrc) {
    setPrevSrc(vendor.image);
    setLogoLoaded(false);
    setLogoFailed(false);
  }
  const isStorefront = isStorefrontCategoryId(vendor.categoryId);
  const color = isAmenityType(vendor.type)
    ? amenityFill(vendor.type)
    : isStorefront
      ? storefrontFill()
      : category?.color ?? DEFAULT_CATEGORY_COLOR;
  const dataCatId = isAmenityType(vendor.type) ? vendor.type : vendor.categoryId;
  const glyphId = resolveGlyphKey(vendor.type, vendor.categoryId);
  const categoryLabel = category?.name.toUpperCase() ?? '';
  const blurb = vendor.description;
  const hasLogo = Boolean(vendor.image) && !logoFailed;

  const avatarClass = [
    styles.avatar,
    isStorefront && styles.avatarSquare,
    hasLogo && logoLoaded && styles.avatarLogoLoaded,
  ]
    .filter(Boolean)
    .join(' ');
  const avatarLogoClass = [styles.avatarLogo, logoLoaded && styles.avatarLogoVisible]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={styles.snapshot}
      data-cat-id={dataCatId}
    >
      <div className={styles.header}>
        <div className={avatarClass}>
          <CategoryGlyph
            categoryId={glyphId}
            size={40}
            color={legibleOn(color)}
            bgColor={color}
          />
          {hasLogo && (
            <img
              className={avatarLogoClass}
              src={vendor.image}
              alt=""
              loading="lazy"
              decoding="async"
              onLoad={() => setLogoLoaded(true)}
              onError={() => setLogoFailed(true)}
            />
          )}
        </div>
        <div className={styles.titleBlock}>
          <div className={styles.eyebrow}>
            {categoryLabel}
          </div>
          <h3 className={styles.name}>{vendor.name}</h3>
          {locationName && (
            <div className={styles.meta}>
              <LocationTag street={locationName} size="md" />
            </div>
          )}
        </div>
        {onClose && (
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close vendor">
            <XIcon size={14} color="var(--color-text-primary)" />
          </button>
        )}
      </div>

      {blurb && <div id={VENDOR_SNAPSHOT_BLURB_ID} className={styles.blurb}>{blurb}</div>}

      {onViewDetails && (
        <button className={styles.primaryBtn} onClick={() => onViewDetails(vendor)}>
          View Details
        </button>
      )}
    </div>
  );
}
